import { Suspense, useMemo } from 'react'
import { Table } from '@tanstack/react-table'

import { QueryErrorBoundary } from '@/catalog-apps/components/QueryErrorBoundary'
import { ApplicationsBreadcrumb } from '../ApplicationsBreadcrumbs'
import { useCurrentApplication } from '../useCurrentApplication'
import { RestSchemas, useRestClient } from '@/contexts/QueryProvider'
import { appPermissionsTableColumnsV2 } from './AppPermissionsTable/appPermissionsTableColumnsV2'
import { DataTable } from '@/components/ui/DataTable'
import { ApplicationPermissionsForm } from '../Forms/ApplicationPermissionsForm'
import { Button } from '@/components/shadcn/button'
import { IconPlusOutline } from '@icons/IconPlusOutline'
import { DataTableLoading } from '@/catalog-apps/components/component-library/DataTableComponent'
import { TablePageContainer } from '@/components/ui/DataTable/PageTableContainer'
import { MultiSelectCombobox, SelectOption } from '@/components/form-components/MultiSelectCombobox'

type ConsolidatedPermission = RestSchemas['Messages.Applications.ConsolidatedPermission']

export function AppPermissionsPageV2() {
  return (
    <>
      <ApplicationsBreadcrumb title="Permissions" />
      <QueryErrorBoundary errorMessage="Failed to fetch permissions">
        <Suspense fallback={<DataTableLoading />}>
          <AppPermissionsPanelContentV2 />
        </Suspense>
      </QueryErrorBoundary>
    </>
  )
}

function AppPermissionsPanelContentV2() {
  const { applicationId } = useCurrentApplication()
  const { getApplicationsApiGetConsolidatedPermissions } = useRestClient()
  const { data: consolidatedPermissions } = getApplicationsApiGetConsolidatedPermissions.useSuspenseQuery({
    query: { applicationId },
  })
  const roleNameOptions = useMemo<SelectOption<string>[]>(
    () =>
      Array.from(
        new Set(
          consolidatedPermissions
            .map((permission) => permission.roleName)
            .filter((value): value is string => !!value?.trim())
        )
      )
        .sort((a, b) => a.localeCompare(b))
        .map((value) => ({ value, label: value })),
    [consolidatedPermissions]
  )
  const configTypeOptions = useMemo<SelectOption<string>[]>(
    () =>
      Array.from(
        new Set(
          consolidatedPermissions
            .map((permission) => permission.configType)
            .filter((value): value is string => !!value?.trim())
        )
      )
        .sort((a, b) => a.localeCompare(b))
        .map((value) => ({ value, label: value })),
    [consolidatedPermissions]
  )
  const configNameOptions = useMemo<SelectOption<string>[]>(
    () =>
      Array.from(
        new Set(
          consolidatedPermissions
            .map((permission) => permission.configName)
            .filter((value): value is string => !!value?.trim())
        )
      )
        .sort((a, b) => a.localeCompare(b))
        .map((value) => ({ value, label: value })),
    [consolidatedPermissions]
  )

  return (
    <TablePageContainer>
      <DataTable
        columns={appPermissionsTableColumnsV2}
        data={consolidatedPermissions}
        filters={(table) => (
          <Filters
            table={table}
            roleNameOptions={roleNameOptions}
            configTypeOptions={configTypeOptions}
            configNameOptions={configNameOptions}
          />
        )}
        action={
          <ApplicationPermissionsForm
            sheetTitle="Add permissions"
            sheetTriggerTooltip="Add permissions"
            sheetTrigger={
              <Button size="icon" variant="ghost">
                <IconPlusOutline />
                <span className="sr-only">Add permissions</span>
              </Button>
            }
          />
        }
      />
    </TablePageContainer>
  )
}

function Filters({
  table,
  roleNameOptions,
  configTypeOptions,
  configNameOptions,
}: {
  table: Table<ConsolidatedPermission>
  roleNameOptions: SelectOption<string>[]
  configTypeOptions: SelectOption<string>[]
  configNameOptions: SelectOption<string>[]
}) {
  const selectedRoleNames = ((table.getColumn('roleName')?.getFilterValue() as string[] | undefined) ?? []).filter(
    Boolean
  )
  const selectedConfigTypes = ((table.getColumn('configType')?.getFilterValue() as string[] | undefined) ?? []).filter(
    Boolean
  )
  const selectedConfigNames = ((table.getColumn('configName')?.getFilterValue() as string[] | undefined) ?? []).filter(
    Boolean
  )

  return (
    <>
      <div className="w-56">
        <MultiSelectCombobox
          triggerClassName="h-9"
          renderBadgesInTrigger
          options={roleNameOptions}
          selectedValues={selectedRoleNames}
          onSelectionChange={(values) =>
            table.getColumn('roleName')?.setFilterValue(values.length > 0 ? values : undefined)
          }
          placeholder="By Roles"
          searchPlaceholder="Search role names..."
          emptyMessage="No role names found."
        />
      </div>
      <div className="w-56">
        <MultiSelectCombobox
          triggerClassName="h-9"
          renderBadgesInTrigger
          options={configTypeOptions}
          selectedValues={selectedConfigTypes}
          onSelectionChange={(values) =>
            table.getColumn('configType')?.setFilterValue(values.length > 0 ? values : undefined)
          }
          placeholder="By Config Types"
          searchPlaceholder="Search config types..."
          emptyMessage="No config types found."
        />
      </div>
      <div className="w-56">
        <MultiSelectCombobox
          triggerClassName="h-9"
          renderBadgesInTrigger
          options={configNameOptions}
          selectedValues={selectedConfigNames}
          onSelectionChange={(values) =>
            table.getColumn('configName')?.setFilterValue(values.length > 0 ? values : undefined)
          }
          placeholder="By Config Names"
          searchPlaceholder="Search config names..."
          emptyMessage="No config names found."
        />
      </div>
    </>
  )
}
